import { Link, NavLink, Outlet } from 'react-router-dom';
import { useAuth } from '../auth/useAuth';
import './PortalLayout.css';
import { ImpersonationBanner } from './ImpersonationBanner';

export interface PortalNavItem {
  to: string;
  label: string;
  /** Match only the exact path (use for a section's index route). */
  end?: boolean;
}

interface PortalLayoutProps {
  /** Section name shown in the sidebar, e.g. "Organizer". */
  section: string;
  nav: readonly PortalNavItem[];
}

/**
 * Shared shell for every authenticated role portal. One layout, parameterized
 * by section — the six portals differ in their navigation and their data, not
 * in their chrome.
 */
export function PortalLayout({ section, nav }: PortalLayoutProps) {
  const { user, logout } = useAuth();

  const displayName =
    [user?.firstName, user?.lastName].filter(Boolean).join(' ') || user?.email || 'Account';

  return (
    /* The Investor portal is an invest surface, so the whole subtree switches palette
       here rather than each screen remembering to mark itself. Every other portal stays
       on the consumer default. */
    <div className="portal-layout" data-surface={section === 'Investor' ? 'invest' : undefined}>
      <ImpersonationBanner />
      <a className="skip-link" href="#main">
        Skip to content
      </a>

      <aside className="portal-sidebar">
        <div className="portal-sidebar__head">
          <Link to="/" className="portal-sidebar__brand display">
            Voleto
          </Link>
          <p className="portal-sidebar__section">{section}</p>
        </div>

        <nav className="portal-sidebar__nav" aria-label={`${section} navigation`}>
          {nav.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                isActive ? 'portal-sidebar__link is-active' : 'portal-sidebar__link'
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="portal-sidebar__foot">
          <p className="portal-sidebar__user" title={user?.email}>
            {displayName}
          </p>
          <p className="portal-sidebar__role identifier">{user?.role}</p>
          <button
            type="button"
            className="button button--ghost button--block"
            onClick={() => void logout()}
          >
            Sign out
          </button>
        </div>
      </aside>

      <main id="main" className="portal-main">
        <div className="portal-main__inner">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
